import {isPlainObject, isString} from 'lodash';
import {HoistService, XH} from '@xh/hoist/core';
import {MINUTES} from '@xh/hoist/utils/datetime';
import {getDefaultOptions} from '../../core/ServiceUtils';
import {AiChatFailure, AiChatOptions, AiChatRequest, AiChatResponse, AiChatUserUsage} from '../data/AiChatTypes';

/**
 * Access to the backend AI chat over REST: the question itself, the model options and today's
 * usage. Progress of a running question arrives separately, on the `ai_chat` topic of MessageHub,
 * tagged with the request's `requestId`.
 */
export class AiChatService extends HoistService {
    /**
     * Ask a question. Resolves with the answer; rejects with a fetch exception that `toFailure`
     * turns into an error code. Set `request.requestId` to receive progress and to be able to cancel.
     */
    async chatAsync(request: AiChatRequest): Promise<AiChatResponse> {
        return XH.postJson({
            ...getDefaultOptions(),
            url: `${XH.getConf('svcApiUrl')}/ai/chat`,
            body: request,
            // An answer that needs several tool rounds takes far longer than a regular API call
            timeout: 10 * MINUTES,
            autoAbortKey: request.requestId
        });
    }

    /** Cancel a running question, on the server as well. */
    cancel(requestId: string) {
        XH.fetchService.abort(requestId);
    }

    async getOptionsAsync(): Promise<AiChatOptions> {
        return XH.fetchJson({...getDefaultOptions(), url: `${XH.getConf('svcApiUrl')}/ai/chat/options`});
    }

    async getUsageAsync(): Promise<AiChatUserUsage> {
        return XH.fetchJson({...getDefaultOptions(), url: `${XH.getConf('svcApiUrl')}/ai/chat/usage/me`});
    }

    /** What went wrong with a question, from the exception `chatAsync` rejected with. */
    toFailure(e: any): AiChatFailure {
        if (e?.isFetchAborted) return {errorCode: 'cancelled', error: null};

        const status = e?.httpStatus,
            {serverDetails} = e ?? {},
            details = isPlainObject(serverDetails) ? serverDetails : null,
            message = details?.detail ?? e?.message ?? String(e ?? '');

        if (status === 400) return {errorCode: 'invalid_request', error: isString(serverDetails) ? serverDetails : message};
        if (status === 429 && details?.usage) return {errorCode: 'limit_exceeded', error: details.error, usage: details.usage};
        if (status === 429) return {errorCode: 'rate_limited', error: message};
        if (status === 502) return {errorCode: 'model_failed', error: message};
        return {errorCode: 'internal_error', error: message};
    }
}
